import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Link2, Check, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Reveal } from "@/hooks/useReveal";
import { trackCTA } from "@/lib/tracking";


import { HOME_FAQ, type FAQItem } from "@/content/faq";

// Homepage default; other pages pass their own `items`.
const DEFAULT_FAQ: FAQItem[] = HOME_FAQ;

type Props = {
  items?: FAQItem[];
  className?: string;
  eyebrow?: string;
  heading?: string;
  subheading?: string;
  /** Prefix used for the URL hash slug, e.g. "faq" -> #faq-how-do-i-book. */
  slugPrefix?: string;
};

const slugify = (input: string) =>
  input
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

/** De-duplicate slugs deterministically so two similar questions still get unique hashes. */
const buildSlugs = (items: FAQItem[], prefix: string) => {
  const seen = new Map<string, number>();
  return items.map((item) => {
    const base = `${prefix}-${slugify(item.q) || "question"}`;
    const count = seen.get(base) ?? 0;
    seen.set(base, count + 1);
    return count === 0 ? base : `${base}-${count + 1}`;
  });
};

export const FAQSection = ({
  items = DEFAULT_FAQ,
  className,
  eyebrow = "Frequently Asked",
  heading = "Real questions. Honest answers.",
  subheading = "Most of what people ask before working with Dr. Sully — answered up front so you can decide with clarity.",
  slugPrefix = "faq",
}: Props) => {
  const slugs = useMemo(() => buildSlugs(items, slugPrefix), [items, slugPrefix]);
  const [open, setOpen] = useState<Set<string>>(() => new Set(slugs.length ? [slugs[0]] : []));
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const copyRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Sync open state + scroll from URL hash (initial load, back/forward, external links).
  const syncFromHash = useCallback(
    (behavior: ScrollBehavior = "smooth") => {
      const raw = typeof window !== "undefined" ? window.location.hash.replace(/^#/, "") : "";
      if (!raw) return;
      const target = decodeURIComponent(raw);
      if (!slugs.includes(target)) return;
      setOpen((prev) => {
        if (prev.has(target)) return prev;
        const next = new Set(prev);
        next.add(target);
        return next;
      });
      // Wait a tick so the panel is expanded before scrolling.
      requestAnimationFrame(() => {
        const el = itemRefs.current[target];
        if (el) {
          el.scrollIntoView({ behavior, block: "start" });
          const btn = el.querySelector<HTMLButtonElement>("button[aria-expanded]");
          btn?.focus({ preventScroll: true });
        }
      });
    },
    [slugs],
  );

  useEffect(() => {
    // Defer initial sync so layout (reveal animations, images) has settled.
    const t = window.setTimeout(() => syncFromHash("auto"), 60);
    const onHash = () => syncFromHash("smooth");
    window.addEventListener("hashchange", onHash);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("hashchange", onHash);
    };
  }, [syncFromHash]);

  const toggle = (slug: string, question: string, position: number) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) {
        next.delete(slug);
      } else {
        next.add(slug);
        // Only track opens (not closes) so the metric = interest in that question.
        trackCTA("faq_item_open", {
          faq_slug: slug,
          faq_question: question,
          faq_position: position,
          faq_prefix: slugPrefix,
        });
      }
      return next;
    });
  };

  /**
   * WAI-ARIA Accordion keyboard pattern.
   * ArrowDown / ArrowUp cycle between accordion headers, Home / End jump to
   * the first / last header. Applied to both the question trigger and its
   * copy-link button so each column keeps its own vertical rhythm, and
   * ArrowLeft / ArrowRight move between the two controls in a row.
   */
  const focusControl = (index: number, column: "trigger" | "copy") => {
    const count = slugs.length;
    if (!count) return;
    const wrapped = ((index % count) + count) % count;
    const map = column === "trigger" ? triggerRefs : copyRefs;
    map.current[slugs[wrapped]]?.focus();
  };

  const onControlKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
    column: "trigger" | "copy",
  ) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        focusControl(index + 1, column);
        break;
      case "ArrowUp":
        e.preventDefault();
        focusControl(index - 1, column);
        break;
      case "Home":
        e.preventDefault();
        focusControl(0, column);
        break;
      case "End":
        e.preventDefault();
        focusControl(slugs.length - 1, column);
        break;
      case "ArrowRight":
        e.preventDefault();
        focusControl(index, column === "trigger" ? "copy" : "trigger");
        break;
      case "ArrowLeft":
        e.preventDefault();
        focusControl(index, column === "copy" ? "trigger" : "copy");
        break;
      default:
        break;
    }
  };

  const copyLink = async (slug: string, question: string) => {
    const url = `${window.location.origin}${window.location.pathname}${window.location.search}#${slug}`;
    // Update the URL so refresh/back/forward preserve the anchor without a jump.
    window.history.replaceState(null, "", `#${slug}`);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedSlug(slug);
      setAnnouncement(`Link to “${question}” copied to clipboard`);
      window.setTimeout(() => {
        setCopiedSlug((current) => (current === slug ? null : current));
      }, 1800);
    } catch {
      // Clipboard blocked — fall back to selecting the URL via prompt for manual copy.
      setAnnouncement("Clipboard unavailable — copy the link manually");
      window.prompt("Copy link to this question:", url);
    }
  };

  return (
    <section className={cn("py-20 md:py-28 border-t border-border", className)}>
      <div className="container max-w-4xl">
        {/* Single always-mounted live region: announces copy results even when
            the related panel is collapsed (a region rendered conditionally is
            not reliably announced by screen readers). */}
        <div role="status" aria-live="polite" className="sr-only">
          {announcement}
        </div>

        <Reveal variant="up">
          <span className="eyebrow">{eyebrow}</span>
          <h2 className="font-display font-bold uppercase text-white text-3xl md:text-5xl mt-5 leading-[1.05]">
            {heading}
          </h2>
          {subheading && (
            <p className="text-silver text-base md:text-lg mt-5 max-w-2xl leading-relaxed">
              {subheading}
            </p>
          )}
        </Reveal>

        <div className="mt-12 divide-y divide-border border border-border bg-navy/60">
          {items.map((item, i) => {
            const slug = slugs[i];
            const isOpen = open.has(slug);
            const panelId = `${slug}-panel`;
            const btnId = `${slug}-button`;
            const wasCopied = copiedSlug === slug;
            return (
              <div
                key={slug}
                id={slug}
                ref={(el) => {
                  itemRefs.current[slug] = el;
                }}
                className="scroll-mt-24 md:scroll-mt-32"
              >
                <h3 className="flex items-stretch">
                  <button
                    id={btnId}
                    ref={(el) => {
                      triggerRefs.current[slug] = el;
                    }}
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => toggle(slug, item.q, i)}
                    onKeyDown={(e) => onControlKeyDown(e, i, "trigger")}
                    className="flex-1 flex items-center justify-between gap-6 text-left pl-5 md:pl-7 pr-3 py-5 md:py-6 hover:bg-navy-elevated/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
                  >
                    <span className="font-display uppercase text-white text-sm md:text-base tracking-wide">
                      {item.q}
                    </span>
                    <ChevronDown
                      aria-hidden="true"
                      className={cn(
                        "h-4 w-4 text-gold flex-shrink-0 transition-transform duration-300",
                        isOpen && "rotate-180",
                      )}
                    />
                  </button>
                  <button
                    ref={(el) => {
                      copyRefs.current[slug] = el;
                    }}
                    type="button"
                    onClick={() => {
                      trackCTA("faq_copy_link", {
                        faq_slug: slug,
                        faq_question: item.q,
                        faq_position: i,
                        faq_prefix: slugPrefix,
                      });
                      copyLink(slug, item.q);
                    }}
                    onKeyDown={(e) => onControlKeyDown(e, i, "copy")}
                    aria-label={`Copy link to “${item.q}”`}
                    // Reflects transient success state to assistive tech that
                    // reads the button again after activation.
                    aria-live="off"
                    title={wasCopied ? "Link copied" : "Copy link to this question"}
                    className="min-w-11 px-4 md:px-5 flex items-center justify-center text-silver hover:text-gold border-l border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60 transition-colors"
                  >
                    {wasCopied ? (
                      <Check className="h-4 w-4 text-gold" aria-hidden="true" />
                    ) : (
                      <Link2 className="h-4 w-4" aria-hidden="true" />
                    )}
                    <span className="sr-only">
                      {wasCopied ? "Link copied" : "Copy link"}
                    </span>
                  </button>
                </h3>
                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={btnId}
                  hidden={!isOpen}
                  className="px-5 md:px-7 pb-6 text-silver text-sm md:text-base leading-relaxed"
                >
                  <p className="m-0">{item.a}</p>

                  <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2">
                    <Link
                      to={`/strategy?utm_source=faq&utm_medium=inline_cta&utm_campaign=${encodeURIComponent(slugPrefix)}&faq=${encodeURIComponent(slug)}`}
                      onClick={() =>
                        trackCTA("faq_strategy_cta_click", {
                          faq_slug: slug,
                          faq_question: item.q,
                          faq_position: i,
                          faq_prefix: slugPrefix,
                          destination: "/strategy",
                          surface: "faq_answer",
                        })
                      }
                      className="inline-flex items-center gap-2 bg-gold text-navy-deep font-display uppercase tracking-[0.2em] text-[11px] px-4 py-2.5 hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60 transition-all"
                    >
                      Apply for a strategy session
                      <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                    </Link>
                    <span className="text-silver text-[11px] uppercase tracking-[0.2em]">
                      Response within one business day
                    </span>

                  </div>

                  {wasCopied && (
                    <div
                      role="status"
                      aria-live="polite"
                      className="mt-3 text-[11px] uppercase tracking-[0.2em] text-gold"
                    >
                      Link copied to clipboard
                    </div>

                  )}
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-[10px] uppercase tracking-[0.25em] text-silver mt-8">
          Don’t see your question?{" "}
          <a href="/inquire" className="text-gold underline underline-offset-4">
            Send it directly →
          </a>
        </p>

      </div>
    </section>
  );
};
